import { Checkbox as Base } from '@base-ui/react/checkbox'
import { Check } from 'lucide-react'
import { cn } from '../../lib/cn'
import s from './Checkbox.module.css'

/**
 * Case à cocher — report de `.opt .box` du prototype validé (`mocks/ghp-bo/app.css`), où elle
 * marque un agent sélectionné dans la page « Créer une équipe ».
 *
 * **Primitive `@base-ui/react`**, et pas un `<input type="checkbox">` stylé : l'indicateur du
 * prototype est un chevron dessiné dans un carré arrondi, ce qu'un `input` natif ne laisse pas
 * remplacer sans `appearance: none` puis une reconstruction complète — et sans récupérer l'état
 * indéterminé. La primitive publie son état sur le DOM (`data-checked`, `data-unchecked`,
 * `data-disabled`), lu ici **en CSS** : aucun état React n'est transformé en nom de classe.
 *
 * ⚠️ Les trois noms d'attributs viennent de `CheckboxRootDataAttributes` du paquet, pas d'une
 * supposition — `packages/ui/CLAUDE.md` prévient qu'ils varient d'une primitive à l'autre.
 *
 * **Contrôlée seulement** (`checked` + `onCheckedChange`), comme `Modal` l'est pour `open` : la
 * sélection appartient à l'état de l'écran, un état interne en ferait une seconde source de
 * vérité. C'est l'écran qui sait qu'on ne coche que deux agents.
 */

export interface CheckboxProps {
  checked: boolean
  onCheckedChange: (checked: boolean) => void
  /** Rattachement à un `<label>` externe, quand la rangée porte le libellé. */
  id?: string
  /** Rattachement à un libellé déjà rendu ailleurs — le nom de l'agent, par exemple. */
  'aria-labelledby'?: string
  /**
   * Le nom accessible **quand aucun libellé n'est rendu à côté** : une case de sélection en tête
   * de rangée de tableau, dont la colonne n'a pas d'en-tête lisible.
   *
   * ⚠️ À n'employer **que** dans ce cas. Un contrôle qui a un libellé visible se rattache par
   * `id` ou `aria-labelledby` — un `aria-label` en doublon dit une chose à la synthèse vocale et
   * une autre à l'œil, et c'est le second qui a raison.
   */
  'aria-label'?: string
  disabled?: boolean
  className?: string
}

export function Checkbox({
  checked,
  onCheckedChange,
  id,
  'aria-labelledby': labelledBy,
  'aria-label': label,
  disabled,
  className,
}: CheckboxProps) {
  return (
    <Base.Root
      id={id}
      aria-labelledby={labelledBy}
      aria-label={label}
      checked={checked}
      onCheckedChange={onCheckedChange}
      disabled={disabled}
      className={cn(s.root, className)}
    >
      {/* L'indicateur n'est monté que coché : c'est la primitive qui le décide, et le CSS n'a donc
          pas à masquer un chevron toujours présent. */}
      <Base.Indicator className={s.indicator}>
        <Check aria-hidden />
      </Base.Indicator>
    </Base.Root>
  )
}
